import React from 'react';
import { Info } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Runtime active URL: on matchitup.in use window.location.origin so the OAuth
// nonce cookie is set for the SAME domain that receives the callback.
const getActiveBackendURL = () => {
  if (typeof window !== 'undefined' && window.__MIU_ACTIVE_BACKEND_URL__) {
    return window.__MIU_ACTIVE_BACKEND_URL__;
  }
  if (typeof window !== 'undefined' && (window.location?.origin || '').includes('matchitup.in')) {
    return window.location.origin;
  }
  return BACKEND_URL;
};

// UTM / referral params to forward through the OAuth round-trip.
const TRACKING_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'ref', 'referral_code'];

const stashTrackingParams = () => {
  try {
    const sp = new URLSearchParams(window.location.search);
    const payload = {};
    TRACKING_KEYS.forEach((k) => {
      const v = sp.get(k);
      if (v) payload[k] = v;
    });
    // Also preserve anything already stashed (e.g. from landing page)
    const prev = sessionStorage.getItem('miu_signup_tracking');
    if (prev) {
      try {
        Object.assign(payload, JSON.parse(prev), payload);
      } catch (error) {
        console.warn('Failed to merge saved signup tracking', error);
      }
    }
    if (Object.keys(payload).length) {
      sessionStorage.setItem('miu_signup_tracking', JSON.stringify(payload));
    }
  } catch (error) {
    console.warn('Failed to stash signup tracking params', error);
  }
};

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
    <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="#0A66C2"/>
  </svg>
);

const PROVIDERS = [
  { id: 'google',   label: 'Google',   Icon: GoogleIcon },
  { id: 'linkedin', label: 'LinkedIn', Icon: LinkedInIcon },
];

/**
 * Direct OAuth — simple anchor links to backend login endpoints.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 *
 * Optional prop: linkToken — when provided, "link mode" (already-signed-in
 * user is connecting an extra provider from Settings).
 *
 * NOTE: Social OAuth is hidden until a tech developer integrates it properly.
 * Set SOCIAL_AUTH_ENABLED = true to re-enable.
 */
const SOCIAL_AUTH_ENABLED = true;

const SocialAuthButtons = ({ linkToken = null }) => {
  if (!SOCIAL_AUTH_ENABLED) return null;
  const buildHref = (providerId) => {
    const base = `${getActiveBackendURL()}/api/auth/${providerId}/login`;
    return linkToken ? `${base}?link_token=${encodeURIComponent(linkToken)}` : base;
  };

  const handleClick = () => {
    // Stash any landing-page UTM/ref params so SocialDone can replay them to the backend
    stashTrackingParams();
  };

  return (
    <div className="space-y-3" data-testid="social-auth-buttons">
      {PROVIDERS.map((provider) => (
        <a
          key={provider.id}
          href={buildHref(provider.id)}
          onClick={handleClick}
          data-testid={`social-login-${provider.id}`}
          className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 hover:border-slate-300 transition-all duration-200 text-slate-700 font-medium text-sm no-underline"
        >
          <provider.Icon />
          {linkToken ? `Connect ${provider.label}` : `Continue with ${provider.label}`}
        </a>
      ))}

      {/* Helper text to reassure users that social sign-in links safely */}
      {!linkToken && (
        <p
          className="text-xs text-slate-500 text-center mt-1 px-2 leading-relaxed italic"
          data-testid="social-login-helper-text"
        >
          <Info className="inline w-3 h-3 mr-1 -mt-0.5 align-middle" />
          We&apos;ll securely link your social account using your email address.
        </p>
      )}
    </div>
  );
};

export default SocialAuthButtons;
